import { BiScalarSchema, BI_LIMIT } from "./schema.js";
/** RFC-style quoted fields, including embedded newlines, doubled quotes and CRLF. */
export function parseBiCsv(text) {
    const records = [];
    let record = [];
    let cell = "";
    let quoted = false;
    let closed = false;
    const finishCell = () => { record.push(cell); cell = ""; closed = false; };
    const finishRecord = () => {
        finishCell();
        if (record.some((value) => value !== ""))
            records.push(record);
        record = [];
        if (records.length > BI_LIMIT.rows + 1)
            throw new Error(`At most ${String(BI_LIMIT.rows)} data records are allowed.`);
    };
    const input = text.replace(/^\uFEFF/u, "");
    for (let at = 0; at < input.length; at += 1) {
        const char = input[at];
        if (quoted) {
            if (char === '"' && input[at + 1] === '"') {
                cell += '"';
                at += 1;
            }
            else if (char === '"') {
                quoted = false;
                closed = true;
            }
            else
                cell += char;
        }
        else if (char === ",")
            finishCell();
        else if (char === "\n" || char === "\r") {
            if (char === "\r" && input[at + 1] === "\n")
                at += 1;
            finishRecord();
        }
        else if (char === '"' && cell === "" && !closed)
            quoted = true;
        else {
            if (closed || char === '"')
                throw new Error("CSV has an unexpected character around a quoted field.");
            cell += char;
        }
        if (cell.length > BI_LIMIT.cell)
            throw new Error("A CSV value is too long.");
        if (record.length > BI_LIMIT.columns)
            throw new Error("The CSV has too many columns.");
    }
    if (quoted)
        throw new Error("CSV has an unclosed quoted field.");
    if (cell !== "" || record.length > 0 || closed)
        finishRecord();
    const header = records.shift()?.map((value) => value.trim());
    if (header === undefined || header.length === 0 || header.some((value) => value === "") || new Set(header).size !== header.length)
        throw new Error("CSV needs distinct, non-empty column headers.");
    return records.map((values, index) => {
        if (values.length !== header.length)
            throw new Error(`CSV record ${String(index + 2)} has the wrong number of columns.`);
        return Object.fromEntries(header.map((key, column) => [key, scalarOf(values[column])]));
    });
}
function scalarOf(text) {
    // Leading zeros and whitespace are significant in identifiers. Do not infer dates or currencies.
    if (text === "")
        return null;
    if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/u.test(text)) {
        const numeric = Number(text);
        const digits = text.replace(/[-.]/gu, "").replace(/^0+/u, "").length;
        if (((!text.includes(".") && Number.isSafeInteger(numeric)) || digits <= 15) && Number.isFinite(numeric) && Math.abs(numeric) <= Number.MAX_SAFE_INTEGER && (!Number.isInteger(numeric) || Number.isSafeInteger(numeric)))
            return numeric;
    }
    return text;
}
export function assertBiJsonPrecision(text) {
    // Do not silently round high precision JSON numbers before their schema sees them.
    for (const token of text.matchAll(/"(?:[^"\\]|\\.)*"|(-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)/gu)) {
        const numeric = token[1];
        if (numeric === undefined)
            continue;
        const coefficient = numeric.split(/[eE]/u)[0] ?? "";
        const digits = coefficient.replace(/[-.]/gu, "").replace(/^0+/u, "").length;
        if (digits > 15 && (/[.eE]/u.test(numeric) || !Number.isSafeInteger(Number(numeric))))
            throw new Error("Represent high precision JSON numbers as strings.");
    }
}
export function parseBiJson(text, recordsPath = "") {
    assertBiJsonPrecision(text);
    let value = JSON.parse(text);
    if (recordsPath !== "") {
        for (const part of recordsPath.split(".")) {
            if (part === "" || typeof value !== "object" || value === null || !Object.hasOwn(value, part))
                throw new Error("The JSON records path does not exist.");
            value = value[part];
        }
    }
    if (!Array.isArray(value) || value.length === 0 || value.length > BI_LIMIT.rows)
        throw new Error(`JSON must contain 1–${String(BI_LIMIT.rows)} flat records.`);
    return value.map((row) => {
        if (typeof row !== "object" || row === null || Array.isArray(row))
            throw new Error("JSON data must be an array of flat objects.");
        return Object.fromEntries(Object.entries(row).map(([key, cell]) => [key, BiScalarSchema.parse(cell)]));
    });
}
//# sourceMappingURL=parse.js.map